/**
 * @fileoverview Permissions Hook
 * @description Centralized permission checking for the application.
 * Fetches and caches user permissions, provides helper methods.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import type { AppRole } from '@/types/database';

export interface UserPermissions {
  can_add_employee: boolean;
  can_edit_employee: boolean;
  can_delete_employee: boolean;
  can_upload_excel: boolean;
  can_view_payroll: boolean;
  can_process_payroll: boolean;
  can_manage_users: boolean;
  can_view_attendance: boolean;
  can_edit_attendance: boolean;
  can_delete_attendance: boolean;
  can_view_leave: boolean;
  can_approve_leave: boolean;
  can_view_reports: boolean;
  // Extra fine-grained operations
  can_manage_loans: boolean;
  can_manage_advances: boolean;
  can_manage_incentives: boolean;
  can_manage_allowances: boolean;
  can_manage_warnings: boolean;
  can_manage_documents: boolean;
  can_manage_master_data: boolean;
  can_approve_overtime: boolean;
  can_terminate_employee: boolean;
  can_manage_backups: boolean;
  can_view_audit_logs: boolean;
  can_export_data: boolean;
  can_manage_settings: boolean;
  can_manage_holidays: boolean;
  can_manage_cash_paid_leaves: boolean;
  can_print_payslips: boolean;
  can_manage_leave_balances: boolean;
  can_quick_attendance_entry: boolean;
  can_deduct_overtime: boolean;
  // Sidebar menu access
  can_access_employees: boolean;
  can_access_attendance: boolean;
  can_access_leave: boolean;
  can_access_overtime: boolean;
  can_access_payroll: boolean;
  can_access_reports: boolean;
  can_access_master_data: boolean;
  can_access_users: boolean;
  can_access_accommodation: boolean;
  can_access_transport: boolean;
  can_access_warnings: boolean;
  can_access_settings: boolean;
}

const DEFAULT_PERMISSIONS: UserPermissions = {
  can_add_employee: false,
  can_edit_employee: false,
  can_delete_employee: false,
  can_upload_excel: false,
  can_view_payroll: false,
  can_process_payroll: false,
  can_manage_users: false,
  can_view_attendance: true,
  can_edit_attendance: false,
  can_delete_attendance: false,
  can_view_leave: true,
  can_approve_leave: false,
  can_view_reports: false,
  can_access_employees: true,
  can_access_attendance: true,
  can_access_leave: true,
  can_access_overtime: true,
  can_access_payroll: false,
  can_access_reports: false,
  can_access_master_data: false,
  can_access_users: false,
  can_access_accommodation: false,
  can_access_transport: false,
  can_access_warnings: false,
  can_access_settings: false,
  can_manage_loans: false,
  can_manage_advances: false,
  can_manage_incentives: false,
  can_manage_allowances: false,
  can_manage_warnings: false,
  can_manage_documents: false,
  can_manage_master_data: false,
  can_approve_overtime: false,
  can_terminate_employee: false,
  can_manage_backups: false,
  can_view_audit_logs: false,
  can_export_data: false,
  can_manage_settings: false,
  can_manage_holidays: false,
  can_manage_cash_paid_leaves: false,
  can_print_payslips: false,
  can_manage_leave_balances: false,
  can_quick_attendance_entry: false,
  can_deduct_overtime: false,
};

// Role-based default permissions
const ROLE_PERMISSIONS: Record<AppRole, Partial<UserPermissions>> = {
  super_admin: {
    can_add_employee: true, can_edit_employee: true, can_delete_employee: true,
    can_upload_excel: true, can_view_payroll: true, can_process_payroll: true,
    can_manage_users: true, can_view_attendance: true, can_edit_attendance: true,
    can_delete_attendance: true, can_view_leave: true, can_approve_leave: true,
    can_view_reports: true,
    can_manage_loans: true, can_manage_advances: true, can_manage_incentives: true,
    can_manage_allowances: true, can_manage_warnings: true, can_manage_documents: true,
    can_manage_master_data: true, can_approve_overtime: true, can_terminate_employee: true,
    can_manage_backups: true, can_view_audit_logs: true, can_export_data: true,
    can_manage_settings: true, can_manage_holidays: true, can_manage_cash_paid_leaves: true,
    can_print_payslips: true, can_manage_leave_balances: true, can_quick_attendance_entry: true, can_deduct_overtime: true,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: true, can_access_payroll: true, can_access_reports: true,
    can_access_master_data: true, can_access_users: true, can_access_accommodation: true,
    can_access_transport: true, can_access_warnings: true, can_access_settings: true,
  },
  hr_manager: {
    can_add_employee: true, can_edit_employee: true, can_delete_employee: true,
    can_upload_excel: true, can_view_payroll: true, can_process_payroll: true,
    can_manage_users: true, can_view_attendance: true, can_edit_attendance: true,
    can_delete_attendance: true, can_view_leave: true, can_approve_leave: true,
    can_view_reports: true,
    can_manage_loans: true, can_manage_advances: true, can_manage_incentives: true,
    can_manage_allowances: true, can_manage_warnings: true, can_manage_documents: true,
    can_manage_master_data: true, can_approve_overtime: true, can_terminate_employee: true,
    can_manage_backups: true, can_view_audit_logs: true, can_export_data: true,
    can_manage_settings: true, can_manage_holidays: true, can_manage_cash_paid_leaves: true,
    can_print_payslips: true, can_manage_leave_balances: true, can_quick_attendance_entry: true, can_deduct_overtime: true,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: true, can_access_payroll: true, can_access_reports: true,
    can_access_master_data: true, can_access_users: true, can_access_accommodation: true,
    can_access_transport: true, can_access_warnings: true, can_access_settings: true,
  },
  hr_staff: {
    can_add_employee: true, can_edit_employee: true, can_delete_employee: false,
    can_upload_excel: true, can_view_payroll: true, can_process_payroll: false,
    can_manage_users: false, can_view_attendance: true, can_edit_attendance: true,
    can_delete_attendance: false, can_view_leave: true, can_approve_leave: true,
    can_view_reports: true,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true, can_quick_attendance_entry: true, can_deduct_overtime: true,
    can_access_overtime: true, can_access_payroll: true, can_access_reports: true,
    can_access_master_data: true, can_access_users: false, can_access_accommodation: true,
    can_access_transport: true, can_access_warnings: true, can_access_settings: false,
  },
  department_head: {
    can_add_employee: false, can_edit_employee: false, can_delete_employee: false,
    can_upload_excel: false, can_view_payroll: false, can_process_payroll: false,
    can_manage_users: false, can_view_attendance: true, can_edit_attendance: false,
    can_delete_attendance: false, can_view_leave: true, can_approve_leave: true,
    can_view_reports: true,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: true, can_access_payroll: false, can_access_reports: true,
    can_access_master_data: false, can_access_users: false, can_access_accommodation: false,
    can_access_transport: false, can_access_warnings: false, can_access_settings: false,
  },
  supervisor: {
    can_add_employee: false, can_edit_employee: false, can_delete_employee: false,
    can_upload_excel: false, can_view_payroll: false, can_process_payroll: false,
    can_manage_users: false, can_view_attendance: true, can_edit_attendance: false,
    can_delete_attendance: false, can_view_leave: true, can_approve_leave: false,
    can_view_reports: false,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: true, can_access_payroll: false, can_access_reports: false,
    can_access_master_data: false, can_access_users: false, can_access_accommodation: false,
    can_access_transport: false, can_access_warnings: false, can_access_settings: false,
  },
  employee: {
    can_add_employee: false, can_edit_employee: false, can_delete_employee: false,
    can_upload_excel: false, can_view_payroll: false, can_process_payroll: false,
    can_manage_users: false, can_view_attendance: true, can_edit_attendance: false,
    can_delete_attendance: false, can_view_leave: true, can_approve_leave: false,
    can_view_reports: false,
    can_access_employees: false, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: false, can_access_payroll: false, can_access_reports: false,
    can_access_master_data: false, can_access_users: false, can_access_accommodation: false,
    can_access_transport: false, can_access_warnings: false, can_access_settings: false,
  },
  auditor: {
    can_add_employee: false, can_edit_employee: false, can_delete_employee: false,
    can_upload_excel: false, can_view_payroll: true, can_process_payroll: false,
    can_manage_users: false, can_view_attendance: true, can_edit_attendance: false,
    can_delete_attendance: false, can_view_leave: true, can_approve_leave: false,
    can_view_reports: true,
    can_access_employees: true, can_access_attendance: true, can_access_leave: true,
    can_access_overtime: true, can_access_payroll: true, can_access_reports: true,
    can_access_master_data: false, can_access_users: false, can_access_accommodation: false,
    can_access_transport: false, can_access_warnings: false, can_access_settings: false,
  },
};

export function usePermissions() {
  const { user, role, isAdmin } = useAuthContext();
  const [permissions, setPermissions] = useState<UserPermissions>(DEFAULT_PERMISSIONS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(DEFAULT_PERMISSIONS);
      setIsLoading(false);
      return;
    }

    try {
      // First, get role-based defaults
      const roleDefaults = role ? ROLE_PERMISSIONS[role] : {};
      
      // Then fetch any custom permissions from database
      const { data: customPerms } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      // Merge: role defaults + custom overrides (all fields)
      const allKeys = Object.keys(DEFAULT_PERMISSIONS) as (keyof UserPermissions)[];
      const mergedPermissions = { ...DEFAULT_PERMISSIONS, ...roleDefaults } as UserPermissions;
      
      if (customPerms) {
        for (const key of allKeys) {
          if (key in customPerms && (customPerms as any)[key] !== null) {
            mergedPermissions[key] = (customPerms as any)[key];
          }
        }
      }

      setPermissions(mergedPermissions);
    } catch (error) {
      logger.error('Error fetching permissions', 'Permissions', error);
      // Fall back to role-based permissions
      setPermissions({
        ...DEFAULT_PERMISSIONS,
        ...(role ? ROLE_PERMISSIONS[role] : {}),
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, role]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Helper methods for checking permissions
  const can = useCallback((permission: keyof UserPermissions): boolean => {
    // Super admin ALWAYS has full unrestricted access to everything
    if (role === 'super_admin') {
      return true;
    }
    return permissions[permission];
  }, [permissions, role]);

  const canAny = useCallback((...perms: (keyof UserPermissions)[]): boolean => {
    return perms.some(p => can(p));
  }, [can]);

  const canAll = useCallback((...perms: (keyof UserPermissions)[]): boolean => {
    return perms.every(p => can(p));
  }, [can]);

  return {
    permissions,
    isLoading,
    can,
    canAny,
    canAll,
    isAdmin,
    role,
    refetch: fetchPermissions,
  };
}
